import type { ReactNode } from "react";

import { SETTINGS } from "~/shared/lib/design-tokens";
import { cn } from "~/shared/lib/utils";
import { useChatUIStore } from "~/shared/store/useChatUIStore";

import { MobileSettingsSheet } from "~/widgets/chat-settings/MobileSettingsSheet";
import { SettingsPanel } from "~/widgets/chat-settings/SettingsPanel";

interface ChatShellLayoutProps {
    header: ReactNode;
    main: ReactNode;
    overlays?: ReactNode;
}

// Общая обёртка чат-страниц: full-viewport контейнер + header +
// две колонки (main + collapsible aside с SettingsPanel) +
// MobileSettingsSheet. Используется и активным чатом, и empty
// state, чтобы не дублировать каркас.
export function ChatShellLayout({ header, main, overlays }: ChatShellLayoutProps) {
    const settingsOpen = useChatUIStore((s) => s.settingsPanelOpen);
    const setSettingsPanelOpen = useChatUIStore((s) => s.setSettingsPanelOpen);
    const closeSettings = () => setSettingsPanelOpen(false);

    return (
        <div className="flex h-[100dvh] flex-col overflow-hidden bg-zinc-950 text-zinc-100">
            {header}
            {overlays}

            <div className="flex min-h-0 flex-1 overflow-hidden">
                <div className="flex min-h-0 min-w-0 flex-1 flex-col">{main}</div>

                <aside
                    aria-hidden={!settingsOpen}
                    className={cn(
                        "hidden shrink-0 flex-col overflow-hidden md:flex",
                        "border-l border-white/10 bg-zinc-950",
                        "transition-[width] duration-200 ease-out",
                        settingsOpen ? SETTINGS.width : "w-0",
                    )}
                >
                    {settingsOpen && <SettingsPanel showClose onClose={closeSettings} />}
                </aside>
            </div>

            <div className="md:hidden">
                <MobileSettingsSheet />
            </div>
        </div>
    );
}
